// src/modules/Shop/pages/ProductDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  FaShoppingCart,
  FaHeart,
  FaRegHeart,
  FaTruck,
  FaShieldAlt,
  FaUndo,
  FaMinus,
  FaPlus,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';
import { supabase } from '../../../config/supabase';
import { useCart } from '../contexts/CartContext';
import ProductGrid from '../components/ProductGrid';

const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 32px 24px;
`;

const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #666;
  margin-bottom: 32px;

  a {
    color: #666;
    text-decoration: none;

    &:hover {
      color: #00D4AA;
    }
  }

  span {
    color: #999;
  }
`;

const ProductLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  margin-bottom: 64px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const Gallery = styled.div``;

const MainImage = styled.div`
  position: relative;
  aspect-ratio: 1;
  background: #f8f8f8;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 16px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const GalleryNav = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.$direction === 'prev' ? 'left: 16px;' : 'right: 16px;'}
  width: 40px;
  height: 40px;
  background: white;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 200ms;

  &:hover {
    transform: translateY(-50%) scale(1.1);
  }
`;

const Thumbnails = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 8px;
`;

const Thumbnail = styled.button`
  flex-shrink: 0;
  width: 80px;
  height: 80px;
  border: 2px solid ${props => props.$active ? '#00D4AA' : 'transparent'};
  border-radius: 8px;
  overflow: hidden;
  background: #f8f8f8;
  cursor: pointer;
  transition: all 200ms;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &:hover {
    border-color: ${props => props.$active ? '#00D4AA' : '#ddd'};
  }
`;

const ProductInfo = styled.div``;

const Category = styled.span`
  display: inline-block;
  font-size: 12px;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #111;
  margin-bottom: 8px;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const SKU = styled.span`
  font-size: 13px;
  color: #999;
  display: block;
  margin-bottom: 24px;
`;

const PriceContainer = styled.div`
  margin-bottom: 24px;
`;

const Price = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: #111;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const ComparePrice = styled.span`
  font-size: 18px;
  color: #999;
  text-decoration: line-through;
  margin-left: 12px;
`;

const Discount = styled.span`
  display: inline-block;
  background: #ef4444;
  color: white;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 4px;
  margin-left: 12px;
`;

const Installments = styled.p`
  font-size: 14px;
  color: #666;
  margin-top: 8px;
`;

const PixPrice = styled.p`
  font-size: 14px;
  color: #22c55e;
  font-weight: 500;
  margin-top: 4px;

  span {
    font-weight: 700;
  }
`;

const ShortDescription = styled.p`
  font-size: 15px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 24px;
`;

const StockStatus = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 24px;
  background: ${props => props.$inStock ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)'};
  color: ${props => props.$inStock ? '#22c55e' : '#ef4444'};
`;

const QuantitySelector = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;

  label {
    font-size: 14px;
    font-weight: 500;
    color: #333;
  }
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
`;

const QuantityButton = styled.button`
  width: 44px;
  height: 44px;
  background: #f5f5f5;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 200ms;

  &:hover {
    background: #e5e5e5;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuantityValue = styled.span`
  width: 60px;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 32px;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const AddToCartButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: #00D4AA;
  color: #000;
  border: none;
  padding: 16px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 200ms;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 212, 170, 0.3);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const WishlistButton = styled.button`
  width: 56px;
  height: 56px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 200ms;

  svg {
    font-size: 20px;
    color: ${props => props.$active ? '#ef4444' : '#666'};
  }

  &:hover {
    border-color: #ef4444;

    svg {
      color: #ef4444;
    }
  }
`;

const Features = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  padding: 24px;
  background: #f8f8f8;
  border-radius: 12px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Feature = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    font-size: 20px;
    color: #00D4AA;
  }

  div {
    font-size: 13px;

    strong {
      display: block;
      color: #111;
      margin-bottom: 2px;
    }

    span {
      color: #666;
    }
  }
`;

const Tabs = styled.div`
  margin-bottom: 64px;
`;

const TabButtons = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 2px solid #eee;
  margin-bottom: 24px;
  overflow-x: auto;
`;

const TabButton = styled.button`
  padding: 16px 24px;
  background: none;
  border: none;
  font-size: 15px;
  font-weight: 500;
  color: ${props => props.$active ? '#111' : '#666'};
  cursor: pointer;
  position: relative;
  white-space: nowrap;

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    height: 2px;
    background: ${props => props.$active ? '#00D4AA' : 'transparent'};
  }

  &:hover {
    color: #111;
  }
`;

const TabContent = styled.div`
  font-size: 15px;
  color: #666;
  line-height: 1.8;

  h3 {
    color: #111;
    font-size: 18px;
    margin-bottom: 16px;
  }

  ul {
    list-style: disc;
    padding-left: 24px;
    margin-bottom: 16px;
  }

  li {
    margin-bottom: 8px;
  }
`;

const RelatedSection = styled.section`
  h2 {
    font-size: 24px;
    font-weight: 700;
    color: #111;
    margin-bottom: 24px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: #666;
`;

const formatPrice = (price) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(price);
};

const ProductDetail = () => {
  const { slug } = useParams();
  const { addItem } = useCart();

  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [activeTab, setActiveTab] = useState('description');
  const [wishlisted, setWishlisted] = useState(false);

  useEffect(() => {
    fetchProduct();
  }, [slug]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('shop_products')
        .select('*, category:shop_categories(name, slug)')
        .eq('slug', slug)
        .single();

      if (error) throw error;

      setProduct(data);

      // Buscar produtos relacionados
      if (data?.category_id) {
        const { data: related } = await supabase
          .from('shop_products')
          .select('*, category:shop_categories(name, slug)')
          .eq('category_id', data.category_id)
          .neq('id', data.id)
          .eq('is_active', true)
          .limit(4);

        setRelatedProducts(related || []);
      }
    } catch (error) {
      console.error('Erro ao buscar produto:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (product && product.stock > 0) {
      addItem(product, quantity);
    }
  };

  const handleQuantityChange = (delta) => {
    const newQuantity = quantity + delta;
    if (newQuantity >= 1 && newQuantity <= (product?.stock || 1)) {
      setQuantity(newQuantity);
    }
  };

  const handleImageNav = (direction) => {
    const images = product?.images || [];
    if (direction === 'prev') {
      setSelectedImage(prev => (prev === 0 ? images.length - 1 : prev - 1));
    } else {
      setSelectedImage(prev => (prev === images.length - 1 ? 0 : prev + 1));
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>Carregando...</LoadingContainer>
      </PageContainer>
    );
  }

  if (!product) {
    return (
      <PageContainer>
        <LoadingContainer>Produto nao encontrado</LoadingContainer>
      </PageContainer>
    );
  }

  const images = product.images?.length > 0
    ? product.images
    : ['https://placehold.co/600x600/f8f8f8/999?text=Produto'];

  const discount = product.compare_price
    ? Math.round((1 - product.price / product.compare_price) * 100)
    : 0;

  const installments = Math.min(12, Math.floor(product.price / 100));
  const installmentValue = installments > 0 ? product.price / installments : product.price;
  const pixPrice = product.price * 0.95; // 5% desconto no Pix

  return (
    <PageContainer>
      <Breadcrumb>
        <Link to="/">Inicio</Link>
        <span>/</span>
        {product.category && (
          <>
            <Link to={`/categoria/${product.category.slug}`}>{product.category.name}</Link>
            <span>/</span>
          </>
        )}
        <span>{product.name}</span>
      </Breadcrumb>

      <ProductLayout>
        <Gallery>
          <MainImage>
            <img src={images[selectedImage]} alt={product.name} />
            {images.length > 1 && (
              <>
                <GalleryNav $direction="prev" onClick={() => handleImageNav('prev')}>
                  <FaChevronLeft />
                </GalleryNav>
                <GalleryNav $direction="next" onClick={() => handleImageNav('next')}>
                  <FaChevronRight />
                </GalleryNav>
              </>
            )}
          </MainImage>

          {images.length > 1 && (
            <Thumbnails>
              {images.map((img, index) => (
                <Thumbnail
                  key={index}
                  $active={selectedImage === index}
                  onClick={() => setSelectedImage(index)}
                >
                  <img src={img} alt={`${product.name} - ${index + 1}`} />
                </Thumbnail>
              ))}
            </Thumbnails>
          )}
        </Gallery>

        <ProductInfo>
          {product.category && <Category>{product.category.name}</Category>}
          <Title>{product.name}</Title>
          {product.sku && <SKU>SKU: {product.sku}</SKU>}

          <PriceContainer>
            <Price>
              {formatPrice(product.price)}
              {product.compare_price && product.compare_price > product.price && (
                <>
                  <ComparePrice>{formatPrice(product.compare_price)}</ComparePrice>
                  <Discount>-{discount}%</Discount>
                </>
              )}
            </Price>
            {installments > 1 && (
              <Installments>
                ou {installments}x de {formatPrice(installmentValue)} sem juros
              </Installments>
            )}
            <PixPrice>
              <span>{formatPrice(pixPrice)}</span> no Pix (5% off)
            </PixPrice>
          </PriceContainer>

          {product.short_description && (
            <ShortDescription>{product.short_description}</ShortDescription>
          )}

          <StockStatus $inStock={product.stock > 0}>
            {product.stock > 0 ? `${product.stock} unidades em estoque` : 'Produto esgotado'}
          </StockStatus>

          <QuantitySelector>
            <label>Quantidade:</label>
            <QuantityControls>
              <QuantityButton onClick={() => handleQuantityChange(-1)} disabled={quantity <= 1}>
                <FaMinus />
              </QuantityButton>
              <QuantityValue>{quantity}</QuantityValue>
              <QuantityButton onClick={() => handleQuantityChange(1)} disabled={quantity >= product.stock}>
                <FaPlus />
              </QuantityButton>
            </QuantityControls>
          </QuantitySelector>

          <Actions>
            <AddToCartButton onClick={handleAddToCart} disabled={product.stock === 0}>
              <FaShoppingCart />
              {product.stock > 0 ? 'Adicionar ao Carrinho' : 'Produto Esgotado'}
            </AddToCartButton>
            <WishlistButton $active={wishlisted} onClick={() => setWishlisted(!wishlisted)}>
              {wishlisted ? <FaHeart /> : <FaRegHeart />}
            </WishlistButton>
          </Actions>

          <Features>
            <Feature>
              <FaTruck />
              <div>
                <strong>Frete Gratis</strong>
                <span>Compras acima de R$ 500</span>
              </div>
            </Feature>
            <Feature>
              <FaShieldAlt />
              <div>
                <strong>Garantia</strong>
                <span>12 meses de garantia</span>
              </div>
            </Feature>
            <Feature>
              <FaUndo />
              <div>
                <strong>Devolucao</strong>
                <span>7 dias para troca</span>
              </div>
            </Feature>
          </Features>
        </ProductInfo>
      </ProductLayout>

      <Tabs>
        <TabButtons>
          <TabButton $active={activeTab === 'description'} onClick={() => setActiveTab('description')}>
            Descricao
          </TabButton>
          <TabButton $active={activeTab === 'specs'} onClick={() => setActiveTab('specs')}>
            Especificacoes
          </TabButton>
          <TabButton $active={activeTab === 'shipping'} onClick={() => setActiveTab('shipping')}>
            Entrega
          </TabButton>
        </TabButtons>

        <TabContent>
          {activeTab === 'description' && (
            <div>
              <h3>Sobre o Produto</h3>
              <p>{product.description || 'Descricao do produto em breve.'}</p>
            </div>
          )}

          {activeTab === 'specs' && (
            <div>
              <h3>Especificacoes Tecnicas</h3>
              <ul>
                {product.weight_kg && <li>Peso: {product.weight_kg} kg</li>}
                {product.height_cm && <li>Altura: {product.height_cm} cm</li>}
                {product.width_cm && <li>Largura: {product.width_cm} cm</li>}
                {product.length_cm && <li>Comprimento: {product.length_cm} cm</li>}
                {product.sku && <li>SKU: {product.sku}</li>}
              </ul>
            </div>
          )}

          {activeTab === 'shipping' && (
            <div>
              <h3>Informacoes de Entrega</h3>
              <ul>
                <li>Frete gratis para compras acima de R$ 500</li>
                <li>Entrega em todo Brasil via Correios e transportadoras</li>
                <li>Prazo de entrega varia conforme a regiao</li>
                <li>Rastreamento disponivel apos o envio</li>
              </ul>
            </div>
          )}
        </TabContent>
      </Tabs>

      {relatedProducts.length > 0 && (
        <RelatedSection>
          <h2>Produtos Relacionados</h2>
          <ProductGrid products={relatedProducts} />
        </RelatedSection>
      )}
    </PageContainer>
  );
};

export default ProductDetail;
